// Aliased: the exported component below is also called SiteDiagnosis, and the verdict type is only
// ever used to key the explanation table.
import type { SiteDiagnosisResult, SiteDiagnosis as Verdict } from '@guard-dog/contracts';
import { useState } from 'react';
import { api } from '../api/client.js';

/**
 * "Why is this site quiet?" — on demand, for one site.
 *
 * A stale timestamp is the least informative fact the console holds. Three unrelated causes produced
 * an identical stale timestamp on real sites on 2026-08-03 — a host 403'ing the WordPress loopback,
 * cron disabled during unrelated troubleshooting, and a low-traffic site nobody had visited. None was
 * a security event, and telling them apart took SSH, WP-CLI and three wrong theories.
 *
 * Operator-triggered rather than automatic: it makes an outbound request to a customer's origin, and
 * a background sweep across a fleet is a different decision with different consent.
 */

/** What each verdict means and what to do — the console must never show a bare enum. */
const EXPLANATION: Record<Verdict, { label: string; tone: string; next: string }> = {
  reporting: {
    label: 'Reporting normally',
    tone: 'ok',
    next: 'This site has checked in recently. Nothing to investigate.',
  },
  site_unreachable: {
    label: 'Site unreachable',
    tone: 'bad',
    next: 'We could not reach the site at all — it may be down, or DNS/TLS may be failing. Check the site itself first; Guard Dog cannot report on a site that is not serving.',
  },
  connector_absent: {
    label: 'Connector not responding',
    tone: 'bad',
    // Deliberately does NOT assert "not installed". Verified against the live fleet on 2026-08-03:
    // a site running the current connector and reporting normally still answered `rest_no_route`,
    // because its `guard-dog/v1` namespace was not exposed. Two very different causes look identical
    // from outside, and telling an operator to reinstall a working connector is worse than saying
    // less.
    next: 'The site is up, but the connector’s status endpoint did not answer. Either the connector is deactivated, or something on the site is blocking its REST routes. Check the Plugins screen first, then anything that restricts /wp-json.',
  },
  connector_disconnected: {
    label: 'Connector disconnected',
    tone: 'bad',
    next: 'The connector is running but no longer holds a connection to the platform. Reconnect it from the site’s Guard Dog → Cloud page — Reconnect keeps its history; Disconnect would delete it.',
  },
  reporting_blocked: {
    label: 'Enrolled, but not reporting',
    tone: 'warn',
    // The state all three real incidents were in, and the one worth spelling out: the site is
    // healthy and correctly enrolled, so this is not a security finding.
    next: 'The connector is installed and still enrolled — so this is not a sign of compromise. Its scheduled check-in is not reaching us. The usual cause is WP-Cron being blocked or disabled on the host. The site’s Guard Dog → Cloud page shows when it last tried and why it failed.',
  },
  inconclusive: {
    label: 'Inconclusive',
    tone: 'warn',
    next: 'We reached the site but could not interpret the response. The evidence below is the raw answer.',
  },
};

export function SiteDiagnosis({ siteId }: { siteId: string }) {
  const [result, setResult] = useState<SiteDiagnosisResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const { diagnosis } = await api.diagnoseSite(siteId);
      setResult(diagnosis);
    } catch (e) {
      // A failed diagnosis must not look like a diagnosis of failure.
      setError(e instanceof Error ? e.message : 'could not run the check');
    } finally {
      setBusy(false);
    }
  }

  const explained = result ? EXPLANATION[result.diagnosis] : null;

  return (
    <div className="site-diagnosis">
      <button type="button" onClick={run} disabled={busy}>
        {busy ? 'Checking…' : 'Why is this site quiet?'}
      </button>

      {error ? <p className="muted">Could not run the check: {error}</p> : null}

      {result && explained ? (
        <div className={`diagnosis diagnosis-${explained.tone}`}>
          <strong>{explained.label}</strong>
          <p>{explained.next}</p>
          <p className="muted mono">{result.detail}</p>
          <p className="muted">
            Checked {result.checkedAt}
            {result.lastSeenAt ? ` · last check-in ${result.lastSeenAt}` : ' · never checked in'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
